import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo } from 'react';
import { scheduleCartUpsert, useCartHydration, CheckoutData } from '@/integrations/supabase/cart-sync';
import { supabase } from '@/integrations/supabase/client';
import { getStripeEnvironment } from '@/lib/stripe';
import {
  trackAddToCart,
  trackRemoveFromCart,
  trackPromoCodeApplied,
  trackPromoCodeFailed,
} from '@/lib/analytics';
import {
  BUNDLE,
  BUNDLE_PRODUCT_IDS,
  BUNDLE_REQUIREMENTS,
} from '@/lib/products';
import { getBundleUnitPrice, getProductUnitPrice, isProductId } from '@/lib/product-catalog-data';
import {
  evaluatePromotions,
  type PromotionType,
  type CartTotals,
  type AppliedPromotion,
} from '@/lib/promotions';
import { resolveAffiliateCode, setAffiliate } from '@/lib/affiliate';

export interface CartItem {
  id: string;
  name: { de: string; en: string; pt: string };
  price: number;
  quantity: number;
  isBundle?: boolean;
}

export interface PromoCode {
  code: string;
  discountPercent: number;
  promotionType: PromotionType;
  channel?: string | null;
  stripePromotionCodeId: string;
  /** When true, shipping costs are waived entirely for this order. */
  freeShipping?: boolean;
  description: { de: string; en: string };
}

interface CartContextType {
  items: CartItem[];
  /** Adds one unit; for the set, an explicit quantity sets the package size (1/2/3). */
  addItem: (item: Omit<CartItem, 'quantity'>, quantity?: number) => void;
  removeItem: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  clearCart: () => void;
  /** Run the promotion engine and return current totals. */
  getCartTotals: () => CartTotals;
  getItemCount: () => number;
  /** Cart lines with the actual unit price (tier or code applied) for tracking. */
  getTrackedItems: () => Array<{ id: string; name: string; price: number; quantity: number }>;
  hasBundle: () => boolean;
  hasBundleEquivalent: () => boolean;
  promoCode: PromoCode | null;
  applyPromoCode: (code: string) => Promise<{ success: boolean; message: { de: string; en: string } }>;
  removePromoCode: () => void;
  hydrated: boolean;
  checkoutData: CheckoutData | null;
  setCheckoutData: (data: CheckoutData | null) => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const REASON_MESSAGES: Record<string, { de: string; en: string }> = {
  not_found: { de: 'Ungültiger Rabattcode', en: 'Invalid promo code' },
  inactive: { de: 'Rabattcode ist deaktiviert', en: 'Promo code is inactive' },
  expired: { de: 'Rabattcode ist abgelaufen', en: 'Promo code has expired' },
  not_yet_active: { de: 'Rabattcode ist noch nicht gültig', en: 'Promo code is not yet active' },
  max_redemptions_reached: { de: 'Rabattcode wurde bereits aufgebraucht', en: 'Promo code has reached its limit' },
  not_synced_yet: { de: 'Rabattcode wird gerade aktiviert, bitte gleich erneut versuchen', en: 'Promo code is being activated, please try again shortly' },
  already_redeemed: { de: 'Dieser Rabattcode gilt nur für deine erste Bestellung', en: 'This promo code is valid for your first order only' },
  invalid_format: { de: 'Ungültiger Rabattcode', en: 'Invalid promo code' },
  network_error: { de: 'Verbindungsfehler, bitte erneut versuchen', en: 'Connection error, please try again' },
  bundle_excluded: { de: 'Rabattcodes gelten nicht für das bereits rabattierte Komplett-Set.', en: 'Promo codes do not apply to the already discounted Komplett-Set.' },
  reorder_only: { de: 'Dieser Code gilt für Nachbestellungen.', en: 'This code is valid for reorders.' },
  reorder_email_required: { de: 'Dieser Code gilt für Nachbestellungen. Bitte gib zuerst deine E-Mail-Adresse ein.', en: 'This code is valid for reorders. Please enter your e-mail address first.' },
  affiliate_reorder_hint: { de: 'Für Nachbestellungen gilt dein 10-%-Code.', en: 'For reorders, your 10 % code applies.' },
};

const affiliateSetThanks = (name: string) => ({
  de: `Danke, dass du über ${name} zu uns gekommen bist. Das Komplett-Set ist bereits reduziert.`,
  en: `Thank you for coming to us via ${name}. The Komplett-Set is already reduced.`,
});

export const CartProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [rawItems, setItems] = useState<CartItem[]>([]);
  // The set's unit price always follows its quantity tier (1 / 2 / 3+ sets).
  const items = useMemo(
    () => rawItems.map((i) => (i.id === BUNDLE.id ? { ...i, name: BUNDLE.name, isBundle: true, price: getBundleUnitPrice(i.quantity) } : i)),
    [rawItems],
  );
  const [promoCode, setPromoCode] = useState<PromoCode | null>(null);
  const [checkoutData, setCheckoutData] = useState<CheckoutData | null>(null);
  const [hydrated, setHydrated] = useState(false);

  // Hydrate from Supabase on mount (once)
  useCartHydration(setItems, setPromoCode, setCheckoutData, hydrated, setHydrated);

  // After hydration, re-validate the promo code to get the correct
  // Stripe Promotion Code ID for the current environment (sandbox vs live).
  // This handles the case where a cart was stored on one environment and
  // loaded on another (e.g. preview → production).
  useEffect(() => {
    if (!hydrated || !promoCode) return;
    const currentEnv = getStripeEnvironment();
    // Only re-validate once; avoid infinite loop by watching hydrated only
    if (promoCode.code) {
      supabase.functions.invoke('validate-promo-code', {
        body: { code: promoCode.code, environment: currentEnv },
      }).then(({ data }) => {
        if (data?.valid && data.stripePromotionCodeId !== promoCode.stripePromotionCodeId) {
          setPromoCode(prev => prev ? { ...prev, stripePromotionCodeId: data.stripePromotionCodeId } : prev);
        }
      }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydrated]);

  // Persist to Supabase (debounced) + localStorage (immediate) whenever items/promo/checkout change
  useEffect(() => {
    if (!hydrated) return;
    scheduleCartUpsert(items, promoCode, checkoutData);
  }, [items, promoCode, checkoutData, hydrated]);

  /* ─── Promotion engine ─── */
  const getCartTotals = useMemo(
    () => (): CartTotals => {
      const engineInput = {
        items,
        promoCode: promoCode
          ? {
              code: promoCode.code,
              promotionType: promoCode.promotionType,
              discountPercent: promoCode.discountPercent,
              stripePromotionCodeId: promoCode.stripePromotionCodeId,
              channel: promoCode.channel ?? null,
            }
          : null,
      };
      return evaluatePromotions(engineInput);
    },
    [items, promoCode],
  );

  const applyPromoCode = async (code: string): Promise<{ success: boolean; message: { de: string; en: string } }> => {
    const normalized = code.trim().toUpperCase();
    if (items.length > 0 && items.every((i) => i.id === BUNDLE.id)) {
      // Partner codes on a set-only cart: no discount, but the order is attributed.
      const partner = normalized ? await resolveAffiliateCode(normalized) : null;
      if (partner) {
        setAffiliate(partner.slug, partner.name, 'code');
        return { success: true, message: affiliateSetThanks(partner.name) };
      }
      trackPromoCodeFailed(normalized, 'bundle_excluded');
      return { success: false, message: REASON_MESSAGES.bundle_excluded };
    }
    if (!normalized) {
      trackPromoCodeFailed(normalized, 'invalid_format');
      return { success: false, message: REASON_MESSAGES.invalid_format };
    }
    try {
      const { data, error } = await supabase.functions.invoke('validate-promo-code', {
        body: {
          code: normalized,
          environment: getStripeEnvironment(),
          itemIds: items.map((i) => i.id),
          ...(checkoutData?.email ? { email: checkoutData.email } : {}),
        },
      });
      if (error || !data) {
        trackPromoCodeFailed(normalized, 'network_error');
        return { success: false, message: REASON_MESSAGES.network_error };
      }
      if (data.affiliate?.slug) setAffiliate(data.affiliate.slug, data.affiliate.name, 'code');
      if (!data.valid) {
        trackPromoCodeFailed(normalized, data.reason ?? 'not_found');
        if (data.reason === 'already_redeemed' && data.affiliate?.kind === 'first') {
          return { success: false, message: REASON_MESSAGES.affiliate_reorder_hint };
        }
        return { success: false, message: REASON_MESSAGES[data.reason] ?? REASON_MESSAGES.not_found };
      }
      const channel: string | null = data.channel ?? null;
      const promotionType: PromotionType = data.promotionType ?? 'first_purchase_individual';
      const shippingOnly = data.freeShipping === true && data.discountPercent === 0;
      const description = shippingOnly
        ? { de: 'Kostenloser Versand', en: 'Free shipping' }
        : {
            // Show only the entered code name and its value.
            de: `${data.code} · ${data.discountPercent}%`,
            en: `${data.code} · ${data.discountPercent}%`,
          };
      setPromoCode({
        code: data.code,
        discountPercent: data.discountPercent,
        promotionType,
        channel,
        stripePromotionCodeId: data.stripePromotionCodeId,
        freeShipping: data.freeShipping === true,
        description,
      });
      trackPromoCodeApplied(normalized);
      return { success: true, message: description };
    } catch {
      return { success: false, message: REASON_MESSAGES.network_error };
    }
  };

  const removePromoCode = () => setPromoCode(null);

  const addItem = (item: Omit<CartItem, 'quantity'>, quantity?: number) => {
    if (item.isBundle) {
      const current = items.find((i) => i.id === item.id)?.quantity ?? 0;
      const next = quantity && quantity > 0 ? quantity : current + 1;
      const added = Math.max(1, next - current);
      trackAddToCart(item.id, BUNDLE.name.de, getBundleUnitPrice(next), added);
      setItems((prev) => {
        // Adding the set removes single products (separate carts), and promo
        // codes never apply to it.
        const filtered = prev.filter(i => !(BUNDLE_PRODUCT_IDS as string[]).includes(i.id) && i.id !== item.id);
        return [...filtered, { ...item, quantity: next }];
      });
      setPromoCode(null);
      return;
    }

    // Single products: an explicit quantity adds that many pieces (2/3 packages).
    const add = quantity && quantity > 0 ? Math.floor(quantity) : 1;
    const nextQty = (items.find((i) => i.id === item.id)?.quantity ?? 0) + add;
    // Track the actual unit price after the quantity tier (2 = −5 %, 3+ = −10 %).
    trackAddToCart(item.id, item.name.de, isProductId(item.id) ? getProductUnitPrice(item.id, nextQty) : item.price, add);
    setItems((prev) => {
      // Adding individual item — remove bundle if present
      const filtered = prev.filter(i => i.id !== 'bundle');
      const existing = filtered.find(i => i.id === item.id);
      if (existing) {
        return filtered.map(i =>
          i.id === item.id ? { ...i, quantity: i.quantity + add } : i
        );
      }
      return [...filtered, { ...item, quantity: add }];
    });
  };

  const removeItem = (id: string) => {
    const item = items.find(i => i.id === id);
    if (item) trackRemoveFromCart(id, item.name.de, getCartTotals().lines[id]?.unit ?? item.price);
    setItems((prev) => prev.filter(i => i.id !== id));
  };

  const updateQuantity = (id: string, quantity: number) => {
    if (quantity <= 0) {
      removeItem(id);
      return;
    }
    setItems((prev) =>
      prev.map(item => item.id === id ? { ...item, quantity } : item)
    );
  };

  const clearCart = () => {
    setItems([]);
    setPromoCode(null);
  };

  const getTrackedItems = () => {
    const { lines } = getCartTotals();
    return items.map((i) => ({ id: i.id, name: i.name.de, price: lines[i.id]?.unit ?? i.price, quantity: i.quantity }));
  };

  const getItemCount = () => {
    return items.reduce((count, item) => count + item.quantity, 0);
  };

  const hasBundle = () => {
    return items.some(item => item.id === 'bundle');
  };

  const hasBundleEquivalent = () => {
    return BUNDLE_PRODUCT_IDS.every(id => {
      const item = items.find(i => i.id === id);
      const requiredQty = BUNDLE_REQUIREMENTS[id as keyof typeof BUNDLE_REQUIREMENTS];
      return item && item.quantity >= requiredQty;
    });
  };

  return (
    <CartContext.Provider value={{
      items,
      addItem,
      removeItem,
      updateQuantity,
      clearCart,
      getCartTotals,
      getTrackedItems,
      getItemCount,
      hasBundle,
      hasBundleEquivalent,
      promoCode,
      applyPromoCode,
      removePromoCode,
      hydrated,
      checkoutData,
      setCheckoutData,
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
